<template>
  <b-card class="create-rule">
    <div slot="header">New Alert Rule</div>
    <div>
      <div class="row">
        <div class="col-xl-8 col-lg-12">
          <label for="devices">Devices: </label>
          <multiselect
            v-if="!loading"
            v-model="selectedDevices"
            :options="groupedDevices"
            :multiple="true"
            group-values="devices"
            group-label="type"
            :group-select="true"
            placeholder="Select Devices"
            track-by="name"
            label="name"
            :closeOnSelect="true"
          ><span slot="noResult">Oops! No elements found. Consider changing the search query.</span>
          </multiselect>
        </div>
      </div>

      <div class="rule-conditions" v-if="selectedDevices.length">
        <div class="row">
          <div class="col-xl-4 col-lg-6 col-md-12">
            <label for="rule-label">Alert Name: </label>
            <b-form-input name="rule-label" v-model.trim.lazy="rule.label" v-validate="'required|min:3|max:32'" :class="{'is-invalid': errors.has('rule-label') }" />
            <b-form-text class="is-invalid help" v-show="errors.has('rule-label')">
              {{ errors.first('rule-label') }}
            </b-form-text>
          </div>
        </div>
        <div class="row">
          <div class="col-xl-8 col-lg-12">

            <label for="devices">Send Alerts To: </label>
            <multiselect
              v-if="targets.length"
              v-model="selectedTargets"
              :options="targets"
              :multiple="true"
              placeholder="Select Alert Targets"
              track-by="id"
              label="label"
              :custom-label="targetLabel"
              :closeOnSelect="true"
            ><span slot="noResult">Oops! No targets found.</span>
            </multiselect>
            <div v-else>No targets found. <router-link :to="{ name: 'Notification Targets' }">Create one</router-link> or continue to show alerts on dashboard only.</div>
          </div>
        </div>
        <div class="row">
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="operator">Aggregation: </label>
            <b-form-select v-model="rule.aggPeriod">
              <option value="0">Live (no aggregation)</option>
              <option value="900">15 Minute Avg.</option>
              <option value="3600">Hourly Avg.</option>
              <option value="86400">Daily Avg.</option>
            </b-form-select>
            <b-form-text v-if="rule.aggPeriod == 0" class="text-warning">
              <i class="fa fa-bolt mr-1"></i> Fires on every incoming reading
            </b-form-text>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="metric">Metric: </label>
            <b-form-select
              name="rule-metric"
              v-model="rule.metricName"
              v-validate="'required'"
               :class="{'is-invalid': errors.has('rule-metric') }"
            >
              <option :value="null">Select a Metric</option>
              <option
                v-for="dm in deviceMetrics[selectedDeviceType]"
                :value="dm.value"
                :key="dm.value">{{ dm.text }}</option>
            </b-form-select>
            <b-form-text class="is-invalid help" v-show="errors.has('rule-metric')">
              {{ errors.first('rule-metric') }}
            </b-form-text>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="operator">Operator: </label>
            <b-form-select v-model="rule.comparator">
              <option value=">">Greater Than</option>
              <option value="<">Less Than</option>
            </b-form-select>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="value">Value: </label>
            <b-form-input name="rule-value" v-model.trim.lazy="rule.triggerValue" v-validate="'required|decimal'" :class="{'is-invalid': errors.has('rule-value') }" />
            <b-form-text class="is-invalid help" v-show="errors.has('rule-value')">
              {{ errors.first('rule-value') }}
            </b-form-text>
          </div>
        </div>
        <div class="row">
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="alertReminder">Alert Reminder: </label>
            <b-form-select v-model="rule.reminderInterval">
              <option value="0">Never</option>
              <option value="3600">Every Hour</option>
              <option value="21600">Every 6 Hours</option>
              <option value="43200">Every 12 Hours</option>
              <option value="86400">Daily</option>
            </b-form-select>
          </div>
          <div class="col-xl-2 col-lg-3 col-md-6">
            <label for="alertExpiration">Alert Expiration: </label>
            <b-form-select v-model="rule.expiresIn">
              <option value="0">Never</option>
              <option value="86400">1 Day</option>
              <option value="259200">3 Days</option>
              <option value="604800">1 Week</option>
            </b-form-select>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <RuleActions v-model="stagedActions" :rule-id="null" />
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-lg-2">
            <b-button class="rule-submit" type="submit" variant="primary" @click.prevent="createRule">Create Rule</b-button>
          </div>
        </div>
      </div>

    </div>
  </b-card>
</template>

<script>
import { DEVICES_QUERY, CREATE_RULE_MUTATION, TARGETS_QUERY, CREATE_RULE_ACTION_MUTATION } from '@/constants/graphql'
import RuleActions from './RuleActions.vue'
import { DEVICE_METRICS } from '@/constants/data'
import Multiselect from 'vue-multiselect'
import { Validator } from 'vee-validate'
import CreateRule from '@/components/rules/CreateRule.vue'

const dictionary = {
  en: {
    attributes: {
      'rule-value': 'value',
      'rule-label': 'rule name'
    }
  }
}

Validator.localize(dictionary)

export default {
  name: 'rules',
  data () {
    return {
      devices: [],
      rule: {
        label: null,
        metricName: null,
        aggFunction: 'mean',
        aggPeriod: '900',
        comparator: '>',
        triggerValue: null,
        reminderInterval: 86400,
        expiresIn: 259200
      },
      selectedDevices: [],
      selectedTargets: [],
      stagedActions: [],
      loading: 0,
      deviceMetrics: DEVICE_METRICS
    }
  },
  watch: {
    selectedDevices () {
      let deviceType = this.selectedDeviceType
      for (let d in this.selectedDevices) {
        if (this.selectedDevices[d].type !== deviceType) {
          this.$swal.fire({ icon: 'warning', title: 'You can only create a rule for one device type at a time.', timer: 5000 })
          this.selectedDevices.splice(d, 1)
        }
      }
    }
  },
  computed: {
    deviceNames () {
      return this.devices.map(d => d.name)
    },
    selectedDeviceType () {
      return (this.selectedDevices.length) ? this.selectedDevices[0].type : null
    },
    groupedDevices () {
      let groups = {}
      let options = []

      if (this.devices) {
        this.devices.forEach((device) => {
          let d = groups.hasOwnProperty(this.ucfirst(device.type)) ? groups[this.ucfirst(device.type)] : groups[this.ucfirst(device.type)] = []
          d.push({ name: `${device.site.name} - ${device.name}`, serial: device.serial, type: device.type, id: device.id })
        })
        for (let type in groups) {
          if (groups.hasOwnProperty(type)) {
            options.push({ type, devices: groups[type] })
          }
        }
      }
      return options
    }
  },
  created () {
  },
  methods: {
    targetLabel (target) {
      return `${target.label} - ${target.destination}`
    },
    ucfirst (str) {
      return str.charAt(0).toUpperCase() + str.substr(1)
    },
    // Returns null on success, or a message when the rule saved but its actions did not.
    saveStagedActions (ruleId) {
      var self = this
      if (!ruleId || !this.stagedActions.length) return Promise.resolve(null)
      var chain = Promise.resolve()
      this.stagedActions.forEach(function (action) {
        chain = chain.then(function () {
          return self.$apollo.mutate({
            mutation: CREATE_RULE_ACTION_MUTATION,
            variables: {
              ruleId: ruleId,
              input: { type: action.type, params: action.params, enabled: action.enabled !== false }
            }
          })
        })
      })
      return chain.then(function () { return null }).catch(function (error) {
        return error.message || 'Unknown error'
      })
    },
    createRule () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          let devices = this.selectedDevices.map(d => { return { serial: d.serial } })
          let targets = this.selectedTargets.map(d => { return { id: d.id } })

          let data = { ...this.rule }
          data.devices = {
            connect: devices
          }
          data.targets = {
            connect: targets
          }

          data.type = 'metric'
          data.enabled = true
          data.aggPeriod = parseInt(data.aggPeriod)
          data.aggFunction = data.aggPeriod === 0 ? 'raw' : 'mean'
          data.triggerValue = parseFloat(data.triggerValue)
          data.reminderInterval = parseInt(data.reminderInterval)
          data.expiresIn = parseInt(data.expiresIn)

          this.$apollo.mutate({
            mutation: CREATE_RULE_MUTATION,
            variables: { data }
          }).then((result) => {
            return this.saveStagedActions(result.data.createRule.id)
          }).then((actionError) => {
            this.loading = false
            if (actionError) {
              // The rule exists; only its actions failed. Say so rather than reporting
              // a blanket failure the user would retry into a duplicate rule.
              this.$swal.fire({
                icon: 'warning',
                title: 'Rule created, but its actions failed to save',
                text: actionError + ' — add them from Edit Rule.'
              })
            } else {
              this.$swal.fire({ icon: 'success', title: 'Success! Rule Created' })
            }
            this.$router.push({ name: 'Rules' })
          }).catch((error) => {
            this.loading = false
            this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
          })

          /*
          let rules = []
          this.selectedDevices.forEach((device) => {
            let ruleObj = {...this.rule}
            ruleObj.device = { connect: { id: device.id } }
            ruleObj.targets = {
              connect: {

              }
            }
            ruleObj.aggPeriod = parseInt(ruleObj.aggPeriod)
            ruleObj.triggerValue = parseFloat(ruleObj.triggerValue)
            ruleObj.reminderInterval = parseInt(ruleObj.reminderInterval)
            ruleObj.expiresIn = parseInt(ruleObj.expiresIn)
            rules.push(ruleObj)
          })

          rules.forEach((rule) => {
            this.$apollo.mutate({
              mutation: CREATE_RULE_MUTATION,
              variables: { data: rule }
            }).then(() => {
              this.loading = false
              this.$swal.fire({ icon: 'success', title: 'Success! Rule Created' })
              this.$router.push({ name: 'Rules' })
            }).catch((error) => {
              this.loading = false
              this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
            })
          })
          */
        }
      })
    }
  },
  components: {
    Multiselect,
    RuleActions,
    CreateRule
  },
  apollo: {
    targets: {
      query: TARGETS_QUERY
    },
    devices: {
      query: DEVICES_QUERY
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.multiselect {
  margin-bottom: 20px;
}
.rule-conditions .row {
  margin-bottom: 20px;
}
.rule-submit {
  margin-top: 28px;
}
</style>
